import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Clock } from '@nexdel/core/utils/icons';
import { api, ApiError } from '@nexdel/core/api';
import { useAuth } from '@nexdel/core/auth';
import {
  PageShell,
  ContextCard,
  SubmittedFeedbackCard,
  FeedbackForm,
  ThanksState,
  type FeedbackContext,
} from './feedback/FeedbackForm';

interface InternalFeedbackContext extends FeedbackContext {
  participantId: string;
  interviewId: string;
  propositionId: string;
  available: boolean;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; httpStatus: number; message: string }
  | { status: 'ready'; ctx: InternalFeedbackContext };

/**
 * Authenticated focused feedback page. Mirrors ExternalFeedback in shape, but
 * the participant ID identifies *who you are on the interview* — auth happens
 * via the standard Firebase token. The backend returns 403 if you're not the
 * listed user.
 *
 * Standalone shell (no AppLayout) so the interviewer can land here from an
 * email link without context-switching into the rest of the app.
 */
export default function InternalFeedback() {
  const { participantId } = useParams<{ participantId: string }>();
  const { t, i18n } = useTranslation();
  const { firebaseUser } = useAuth();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [submittedJustNow, setSubmittedJustNow] = useState(false);

  // Wait for Firebase to confirm auth state before fetching.
  const authReady = firebaseUser !== undefined;
  const isAuthed = !!firebaseUser;

  useEffect(() => {
    if (!authReady || !isAuthed || !participantId) return;
    let cancelled = false;
    (async () => {
      try {
        const ctx = await api.get<InternalFeedbackContext>(
          `/api/me/interview-feedback/${participantId}`,
        );
        if (!cancelled) setState({ status: 'ready', ctx });
      } catch (err) {
        if (cancelled) return;
        const httpStatus = err instanceof ApiError ? err.status : 0;
        const message = err instanceof Error ? err.message : 'Failed to load feedback request';
        setState({ status: 'error', httpStatus, message });
      }
    })();
    return () => { cancelled = true; };
  }, [authReady, isAuthed, participantId]);

  if (!authReady) {
    return <PageShell><div className="text-sm text-muted-foreground">{t('common.loading')}</div></PageShell>;
  }

  if (!isAuthed) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(`/feedback/${participantId}`)}`} replace />;
  }

  if (state.status === 'loading') {
    return <PageShell><div className="text-sm text-muted-foreground">{t('common.loading')}</div></PageShell>;
  }

  if (state.status === 'error') {
    return (
      <PageShell>
        <ErrorState httpStatus={state.httpStatus} message={state.message} t={t} />
      </PageShell>
    );
  }

  const { ctx } = state;
  const fmtDateTime = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  if (submittedJustNow) {
    return (
      <PageShell>
        <ThanksState
          ctx={ctx}
          title={t('internalFeedback.thanksTitle')}
          body={t('internalFeedback.thanksBody')}
        />
      </PageShell>
    );
  }

  if (ctx.alreadySubmitted) {
    return (
      <PageShell>
        <AlreadySubmittedState ctx={ctx} fmtDateTime={fmtDateTime} t={t} />
      </PageShell>
    );
  }

  if (!ctx.available) {
    return (
      <PageShell>
        <NotYetState ctx={ctx} fmtDateTime={fmtDateTime} t={t} />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <FeedbackForm
        ctx={ctx}
        submitUrl={`/api/propositions/${ctx.propositionId}/interviews/${ctx.interviewId}/participants/${ctx.participantId}/feedback`}
        method="PUT"
        fmtDateTime={fmtDateTime}
        onSubmitted={() => setSubmittedJustNow(true)}
        t={t}
        title={t('internalFeedback.title')}
        intro={t('internalFeedback.intro')}
        contextLabel={t('internalFeedback.contextLabel')}
        submitButton={t('internalFeedback.submitButton')}
      />
    </PageShell>
  );
}

// ── Internal-specific subviews ──────────────────────────────────────────────

function ErrorState({ httpStatus, message, t }: {
  httpStatus: number;
  message: string;
  t: (k: string) => string;
}) {
  const isForbidden = httpStatus === 403;
  const isNotFound = httpStatus === 404;
  return (
    <div className="text-center space-y-3 py-6">
      <XCircle className="mx-auto text-destructive" size={36} />
      <h1 className="text-lg font-semibold text-foreground">
        {isForbidden ? t('internalFeedback.notListedTitle')
          : isNotFound ? t('internalFeedback.notListedTitle')
          : t('internalFeedback.errorTitle')}
      </h1>
      <p className="text-sm text-muted-foreground">
        {isForbidden ? t('internalFeedback.notListedBody')
          : isNotFound ? t('internalFeedback.notListedBody')
          : message}
      </p>
    </div>
  );
}

function NotYetState({ ctx, fmtDateTime, t }: {
  ctx: InternalFeedbackContext;
  fmtDateTime: (iso: string | null) => string;
  t: (k: string) => string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Clock className="text-muted-foreground" size={20} />
        <h1 className="text-base font-semibold text-foreground">
          {t('internalFeedback.notYetTitle')}
        </h1>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('internalFeedback.notYetBody')}
      </p>
      <ContextCard ctx={ctx} fmtDateTime={fmtDateTime} label={t('internalFeedback.contextLabel')} />
    </div>
  );
}

function AlreadySubmittedState({ ctx, fmtDateTime, t }: {
  ctx: InternalFeedbackContext;
  fmtDateTime: (iso: string | null) => string;
  t: (k: string) => string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="text-emerald-600" size={20} />
        <h1 className="text-base font-semibold text-foreground">
          {t('internalFeedback.alreadySubmittedTitle')}
        </h1>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('internalFeedback.alreadySubmittedBody')}
      </p>
      <ContextCard ctx={ctx} fmtDateTime={fmtDateTime} label={t('internalFeedback.contextLabel')} />
      {ctx.submittedFeedback && <SubmittedFeedbackCard f={ctx.submittedFeedback} t={t} />}
    </div>
  );
}
